// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { AuthUser } from '../../api/types';
import { CASES_PATH } from '../../app/cases';
import type { ThemePreference } from '../../theme';
import { Logo } from '../Logo';
import { ThemeToggle } from '../ThemeToggle';
import { UserMenu } from '../UserMenu';
import styles from './CasesBar.module.css';

export interface CasesBarControlsProps {
  themePreference: ThemePreference;
  onThemeChange: (preference: ThemePreference) => void;
  /** Who is signed in (server mode); omitted in local mode, which has no login. */
  user?: AuthUser;
  onLogout?: () => void;
  /** Signed in through a share link: no account, so no account links. */
  viaShareLink?: boolean;
}

export type CasesBarProps = CasesBarControlsProps;

/**
 * The theme and the user, right-aligned: the bar's right side, and Sager's
 * page header (which has no bar).
 */
export function CasesBarControls({ themePreference, onThemeChange, user, onLogout, viaShareLink }: CasesBarControlsProps) {
  return (
    <div className={styles.right}>
      <ThemeToggle preference={themePreference} setPreference={onThemeChange} />
      {user && onLogout && <UserMenu user={user} onLogout={onLogout} viaShareLink={viaShareLink} />}
    </div>
  );
}

/**
 * The bar over the account pages (Konto, Grupper, Brugere): the Bygning
 * shell's floating panel, with the logo (home: the case list), the theme and
 * the user. The page names itself in its own heading. One row on a phone too.
 * Sager has no bar: its header carries the wordmark and the controls.
 */
export function CasesBar(props: CasesBarProps) {
  return (
    <header className={styles.bar}>
      {/* A plain link, like UserMenu's account links: a page load of its own. */}
      <a className={styles.home} href={CASES_PATH} aria-label="ReUseX — alle sager">
        <Logo size={14} />
      </a>
      <CasesBarControls {...props} />
    </header>
  );
}
